import MapboxGL from './mapbox-gl-loader.es6';

/**
 * Parse a view box string
 * @param {String} box Expected format is "BOX(-3.9990234375 43.19716728250127, 7.55859375 50.764259357116465)"
 * @return {Object|null}
 */
export function parseBox(box) {
    let bounds;
    box.replace(
        /^BOX\(([-0-9.]*) ([-0-9.]*), ?([-0-9.]*) ([-0-9.]*)\)$/i,
        (all, left, bottom, right, top) => {
            bounds = {
                left,
                bottom,
                right,
                top,
                sw: {
                    lat: bottom,
                    lng: left,
                },
                ne: {
                    lat: top,
                    lng: right,
                },
            };
        },
    );
    return bounds;
}

/**
 * Returns a LatLngBounds object from a view box string
 * @param {string} boxstr Expected format is "BOX(-3.9990234375 43.19716728250127, 7.55859375 50.764259357116465)"
 */
export function boundsFromBox(boxstr) {
    const box = parseBox(boxstr);
    if (box && MapboxGL) {
        const sw = new MapboxGL.LngLat(box.sw.lng, box.sw.lat);
        const ne = new MapboxGL.LngLat(box.ne.lng, box.ne.lat);
        return new MapboxGL.LngLatBounds(sw, ne);
    }
    return null;
}

/**
 * Helper to get a bounds string representation
 *
 * @param {mapboxgl.LngLatBounds} bounds
 * @return {string}
 */
export function getBoundsString(bounds) {
    const southWestBound = bounds.getSouthWest();
    const northEastBound = bounds.getNorthEast();
    const boundsString = `${southWestBound.lat},${southWestBound.lng},${northEastBound.lat},${northEastBound.lng}`;
    return boundsString;
}
/**
 *
 * @param {Object|Array} coordinates {lat: Number, lng: Number} or [lat<Number>, lng<Number>]
 * @return {string}
 */
export function coordinatesToString(coordinates) {
    if (coordinates instanceof Array) {
        return `${coordinates[0]},${coordinates[1]}`;
    }
    return `${coordinates.lng},${coordinates.lat}`;
}

/**
 * Calculate distance between 2 points
 * @param {Number} lng1 Longitude of first point
 * @param {Number} lat1 Latitude of first point
 * @param {Number} lng2 Longitude of second point
 * @param {Number} lat2 Latitude of second point
 * @returns {Number}
 */
export function calculateDistance(lng1, lat1, lng2, lat2) {
    const R = 6371e3;
    const lat1Rad = lat1.toRad();
    const lat2Rad = lat2.toRad();
    const deltaLat = (lat2 - lat1).toRad();
    const deltaLng = (lng2 - lng1).toRad();

    const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Distance between 2 points
 * @param {{x: Number, y: Number}} pointA
 * @param {{x: Number, y: Number}} pointB
 * @return {number}
 */
export function pointsDistance(pointA, pointB) {
    const { x: x1, y: y1 } = pointA;
    const { x: x2, y: y2 } = pointB;
    const a = x1 - x2;
    const b = y1 - y2;
    const distance = Math.sqrt(a * a + b * b);
    return distance;
}

/**
 * Compare 2 bounds center and return true or false if the difference is below the given threshold
 * @param {Mapbox.Map} map
 * @param {Mapbox.LngLatBounds} bounds1
 * @param {Mapbox.LngLatBounds} bounds2
 * @param {Number} threshold In pixels
 * @return {boolean}
 */
function boundsCenterHasChanged(map, bounds1, bounds2, threshold = 1) {
    const centerDiff = pointsDistance(
        map.project(bounds1.getCenter()),
        map.project(bounds2.getCenter()),
    );
    return centerDiff >= threshold;
}

/**
 * Compare 2 bounds size and return true or false if the difference is below the given threshold
 * @param {Mapbox.Map} map
 * @param {Mapbox.LngLatBounds} bounds1
 * @param {Mapbox.LngLatBounds} bounds2
 * @param {Number} threshold In pixels
 * @return {boolean}
 */
function boundsSizeHasChanged(map, bounds1, bounds2, threshold = 1) {
    const diagonale1 = pointsDistance(
        map.project(bounds1.getNorthEast()),
        map.project(bounds1.getSouthWest()),
    );
    const diagonale2 = pointsDistance(
        map.project(bounds2.getNorthEast()),
        map.project(bounds2.getSouthWest()),
    );
    const diagonaleDiff = Math.abs(diagonale2 - diagonale1);
    return diagonaleDiff >= threshold;
}

/**
 * Compare 2 bounds in pixel and return true or false if the difference of position of 2 bounds rectangles is below the given threshold
 * @param {Mapbox.Map} map
 * @param {Mapbox.LngLatBounds} bounds1
 * @param {Mapbox.LngLatBounds} bounds2
 * @param {Number} threshold In pixels
 * @return {boolean}
 */
export function shouldChangeBounds(map, bounds1, bounds2, threshold = 1) {
    return (
        boundsCenterHasChanged(map, bounds1, bounds2, threshold) ||
        boundsSizeHasChanged(map, bounds1, bounds2, threshold)
    );
}

/**
 * Extract 80% of closest features (past sales) from a point
 * @param {Object} features
 * @param {{lat: Number, lng: Number}} fromPoint
 * @return {Array<Object>} Colletion of features
 */
export function extractClosestFeatures(features, fromPoint) {
    const sortedFeatures = features
        .filter(
            feature =>
                typeof feature.properties.coordinates !== 'undefined' &&
                feature.properties.coordinates !== null,
        )
        .map(feature => {
            const { coordinates } = feature.properties;
            return {
                ...feature,
                distance: calculateDistance(
                    fromPoint.lat,
                    fromPoint.lng,
                    coordinates.lat,
                    coordinates.lng,
                ),
            };
        })
        .sort((a, b) => a.distance - b.distance);

    const closestFeatures = sortedFeatures.slice(0, Math.ceil((sortedFeatures.length * 80) / 100));
    return closestFeatures;
}

/**
 * Get bounds wrapping a features list
 */
export function getBoundsForFeatures(features) {
    const bounds = new MapboxGL.LngLatBounds();
    features.forEach(
        ({
            properties: {
                coordinates: { lng, lat },
            },
        }) => {
            bounds.extend(new MapboxGL.LngLat(lng, lat));
        },
    );
    return bounds;
}

/**
 * Return if a place Object is an address
 * @param {Objet} place
 * @return {Boolean}
 */
export function placeIsAddress(place) {
    return place && place.geotype_label === 'address';
}

/**
 * Return if a place Object is a street
 * @param {Objet} place
 * @return {Boolean}
 */
export function placeIsStreet(place) {
    return place && place.geotype_label === 'street';
}

/**
 * By injecting geoJson in Mapbox, all features nested properties are stringfied
 * So an object like {"properties": {"id": 1}} will be replace by {"properties": "{\"id\": 1}"}
 * @param feature
 */
export function parseStringifiedFeatures(feature) {
    const newFeature = { ...feature };

    if (feature.properties) {
        Object.keys(feature.properties).forEach(key => {
            const { properties } = feature;
            // If property value has been stringified
            if (
                typeof properties[key] === 'string' &&
                (properties[key].indexOf('{') === 0 || properties[key].indexOf('[') === 0)
            ) {
                try {
                    const cleanValue = JSON.parse(properties[key]);
                    newFeature.properties[key] = cleanValue;
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn(`Unable to parse Mapbox feature JSON : ${e.message}`);
                }
            }
        });
    }

    return newFeature;
}

/**
 * Get first Point feature from a list of features
 * @param {Array<Object>} features
 * @return {Object}
 */
export function getFirstPointFromFeatures(features) {
    if (!Array.isArray(features) || features.length === 0) {
        throw new Error('Features must be an array of at least one element');
    }

    let [feature] = features;

    // In case of a MultiLineString, the first feature contains the LineString geometry
    if (features.length > 1 && feature.geometry.type !== 'Point') {
        feature = features.find(f => f.geometry.type === 'Point');
    }

    return feature;
}
